import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Button,
  SlideProps,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import { Icon } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { GameState, WordNode, WordEdge, RelationType } from '../types';
import { checkWordRelation, checkWordRelationsBatch } from '../utils/wordCheck';
import { SortableWordTile } from './SortableWordTile';
import { WordTile, ArrowContainer } from './StyledComponents';
import Confetti from 'react-confetti';
import { getDailyWords } from '../utils/dailyWords';
import { TransitionProps } from '@mui/material/transitions';
import { keyframes } from '@emotion/react';
// comment
const Edge = styled(ArrowContainer)<{ status: 'valid' | 'invalid' | 'loading' | 'none' }>(
  ({ theme, status }) => ({
    color: status === 'valid'
      ? theme.palette.success.main
      : status === 'invalid'
        ? theme.palette.error.main
        : '#000000',
    '&::before': {
      display: 'none',
    },
    '& .edge-label': {
      position: 'relative',
      zIndex: 1,
      backgroundColor: status === 'valid' 
        ? '#98D87D' 
        : status === 'invalid'
          ? '#FF4911'
          : '#FFD15C',
      padding: theme.spacing(0.25, 1),
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
      color: '#000000',
      border: '3px solid black',
      boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
      transform: 'translate(-2px, -2px)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      minWidth: '32px',
      height: '32px',
      textAlign: 'center',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translate(0px, 0px)',
        boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
      },
      '&:active': {
        transform: 'translate(1px, 1px)',
        boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
        color: 'black',
      }
    },
  })
);

const TitleCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFF4DA',
  padding: theme.spacing(1, 3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 2),
    fontSize: '18px',
  },
  borderRadius: '4px',
  border: '3px solid black',
  boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
  display: 'inline-block',
  fontFamily: '"Public Sans", Arial, sans-serif',
  fontWeight: 900,
  fontSize: '24px',
  letterSpacing: '1px',
  color: 'black',
  whiteSpace: 'nowrap',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  borderBottom: '3px solid black',
  marginBottom: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(3),
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#FFF4DA',
    color: 'black',
    maxWidth: 500,
    margin: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1),
      maxWidth: 'calc(100% - 32px)',
      maxHeight: 'calc(100% - 32px)',
    },
    borderRadius: '8px',
    border: '3px solid black',
    boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)',
    transform: 'translate(-3px, -3px)',
    position: 'relative',
    overflow: 'visible',
  },
  '& .MuiDialogTitle-root': {
    padding: 0,
    position: 'relative',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
    overflowX: 'hidden',
    [theme.breakpoints.down('sm')]: {
      overflowY: 'auto',
    },
    '& > .MuiBox-root': {
      padding: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'min-content',
      },
      '& .MuiTypography-root': {
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.9rem',
        },
      }
    }
  }
}));

const HeaderButton = styled(IconButton)(({ theme }) => ({
  padding: 6,
  width: '32px',
  height: '32px',
  borderRadius: '4px',
  border: '3px solid black',
  backgroundColor: '#FFD15C',
  boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
  transform: 'translate(-2px, -2px)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#FFD15C',
    transform: 'translate(0px, 0px)',
    boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
  },
  '& .material-symbols-outlined': {
    fontVariationSettings: '"FILL" 0, "wght" 700, "GRAD" 0, "opsz" 24',
    fontSize: '20px',
    color: 'black'
  }
}));

const WordInput = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  width: '240px',
  [theme.breakpoints.down('sm')]: {
    width: '85vw',
    maxWidth: '240px',
    padding: theme.spacing(1.5),
  },
  marginTop: theme.spacing(2),
  position: 'relative',
  borderRadius: '8px',
  border: '3px dashed black',
  boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)',
  transform: 'translate(-3px, -3px)',
  backgroundColor: '#FFFFFF',
  '&.attention': {
    animation: `${inputAttention} 0.5s ease-in-out`,
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    padding: theme.spacing(0.5),
    fontFamily: '"Public Sans", Helvetica, Arial, sans-serif',
    fontWeight: 900,
    fontSize: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
    textTransform: 'uppercase',
    color: '#000',
    '&::placeholder': {
      opacity: 1.0,
      fontSize: '1.25rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
      fontWeight: 900,
      letterSpacing: '0px',
      textTransform: 'uppercase',
      color: '#000000',
    },
    '&:focus::placeholder': {
      opacity: 0,
    }
  },
  '& .MuiInput-underline:before, & .MuiInput-underline:after': {
    display: 'none',
  },
}));

const ShareButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#98D87D',
  color: 'black',
  padding: theme.spacing(1, 3),
  borderRadius: '4px',
  border: '3px solid black',
  boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
  transform: 'translate(-2px, -2px)',
  fontFamily: '"Public Sans", Arial, sans-serif',
  fontWeight: 900,
  fontSize: '1rem',
  textTransform: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#98D87D',
    transform: 'translate(0px, 0px)',
    boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
  },
  '& .MuiSvgIcon-root': {
    marginLeft: theme.spacing(1),
  }
}));

const CompletionDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#FFF4DA',
    color: 'black',
    maxWidth: 400,
    margin: theme.spacing(2),
    padding: theme.spacing(3),
    borderRadius: '8px',
    border: '3px solid black',
    boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)',
    transform: 'translate(-3px, -3px)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible',
    marginTop: theme.spacing(2),
  },
  '& .MuiTypography-root': {
    fontFamily: '"Public Sans", Arial, sans-serif',
    fontWeight: 900,
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
  },
  '& .subtitle': {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: theme.spacing(3),
  },
  '& .button-container': {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  '& .MuiIconButton-root': {
    position: 'absolute',
    right: -12,
    top: -12,
    backgroundColor: '#FF4911',
    padding: 6,
    width: '32px',
    height: '32px',
    borderRadius: '4px',
    border: '3px solid black',
    boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)',
    transform: 'translate(-2px, -2px)',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      right: -8,
      top: -8,
      width: '28px',
      height: '28px',
      padding: 4,
    },
    '&:hover': {
      backgroundColor: '#FF4911',
      transform: 'translate(0px, 0px)',
      boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)',
    },
    '& .MuiSvgIcon-root': {
      color: 'black',
      fontSize: '20px',
      [theme.breakpoints.down('sm')]: {
        fontSize: '16px',
      },
    }
  }
}));

const ActionButton = styled(Button)<{ bgColor?: string }>(({ theme, bgColor = '#98D87D' }) => ({
  backgroundColor: bgColor,
  color: 'black',
  padding: theme.spacing(1, 3),
  borderRadius: '4px',
  border: '3px solid black',
  boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
  transform: 'translate(-2px, -2px)',
  fontFamily: '"Public Sans", Arial, sans-serif',
  fontWeight: 900,
  fontSize: '1rem',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: bgColor,
    transform: 'translate(0px, 0px)',
    boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
  },
  '& .MuiSvgIcon-root': {
    marginLeft: theme.spacing(1),
  }
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1) translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.98) translate(-2px, -2px);
    box-shadow: 4px 4px 0px 0px rgba(0,0,0,1);
  }
  100% {
    opacity: 1;
    transform: scale(1) translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
`;

const plusPulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const inputAttention = keyframes`
  0% {
    transform: translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
  20% {
    transform: translate(-5px, -5px);
    box-shadow: 8px 8px 0px 0px rgba(0,0,0,1);
  }
  40% {
    transform: translate(-2px, -2px);
    box-shadow: 4px 4px 0px 0px rgba(0,0,0,1);
  }
  60% {
    transform: translate(-4px, -4px);
    box-shadow: 7px 7px 0px 0px rgba(0,0,0,1);
  }
  80% {
    transform: translate(-2px, -2px);
    box-shadow: 5px 5px 0px 0px rgba(0,0,0,1);
  }
  100% {
    transform: translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
`;

const bounceLeft = keyframes`
  0% { transform: rotate(-3deg) translateY(-2px); }
  20% { transform: rotate(-5deg) translateY(-4px); }
  40% { transform: rotate(-2deg) translateY(-1px); }
  60% { transform: rotate(-4deg) translateY(-3px); }
  80% { transform: rotate(-2.5deg) translateY(-2px); }
  100% { transform: rotate(-3deg) translateY(-2px); }
`;

const bounceRight = keyframes`
  0% { transform: rotate(3deg) translateY(2px); }
  20% { transform: rotate(5deg) translateY(4px); }
  40% { transform: rotate(2deg) translateY(1px); }
  60% { transform: rotate(4deg) translateY(3px); }
  80% { transform: rotate(2.5deg) translateY(2px); }
  100% { transform: rotate(3deg) translateY(2px); }
`;

const Game: React.FC = () => {
  // Parse URL query parameters for custom words
  const getInitialWords = (): [string, string] => {
    const params = new URLSearchParams(window.location.search);
    const customWords = params.get('words');
    
    if (customWords) {
      const [word1, word2] = customWords.split(',');
      if (word1 && word2) {
        return [word1.toLowerCase(), word2.toLowerCase()];
      }
    }
    
    return getDailyWords();
  };

  const [gameState, setGameState] = useState<GameState>(() => {
    const [startWord, endWord] = getInitialWords();
    return {
      nodes: [
        { id: '1', word: startWord },
        { id: '2', word: endWord }
      ],
      edges: [],
      startWord,
      endWord,
      isSolved: false
    };
  });
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [newWord, setNewWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(() => {
    const hasVisited = localStorage.getItem('hasVisitedBefore');
    if (!hasVisited) {
      localStorage.setItem('hasVisitedBefore', 'true');
      return true;
    }
    return false;
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showCopiedSnackbar, setShowCopiedSnackbar] = useState(false);
  const [inputAttentionKey, setInputAttentionKey] = useState(0);
  const [selectedEdgeIndex, setSelectedEdgeIndex] = useState<number | null>(null);
  const [activeExampleIndex, setActiveExampleIndex] = useState(0);
  const [isTitleFlipped, setIsTitleFlipped] = useState(false);
  
  // Track active validations to ignore stale results
  const activeValidationsRef = useRef<Set<string>>(new Set());

  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const handleShare = async () => {
    const wordChain = gameState.nodes.map((node, index) => {
      if (index === gameState.nodes.length - 1) return node.word;
      const relation = gameState.edges[index]?.relation;
      const emoji = relation === 'rhyme' ? ' 🎶 ' : ' 💡 ';
      return node.word + emoji;
    }).join('');
    
    // Use Web Share API only on mobile devices
    if (isMobileDevice() && navigator.share) {
      try {
        await navigator.share({
          title: 'Rhyme or Reason',
          text: wordChain
        });
        return;
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
    
    // Use clipboard for desktop or when Web Share API fails
    navigator.clipboard.writeText(wordChain).then(() => {
      setShowCopiedSnackbar(true);
    }).catch(console.error);
  };

  const handleCloseSnackbar = () => {
    setShowCopiedSnackbar(false);
  };

  useEffect(() => {
    if (gameState.isSolved && !showCompletion) {
      const timer = setTimeout(() => {
        setShowCompletion(true);
      }, 800); // 800ms delay
      return () => clearTimeout(timer);
    }
  }, [gameState.isSolved]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const validateChain = (edges: WordEdge[]): boolean => {
    if (edges.length < 2) return false;
    
    // Check that all edges are valid and match their expected types
    const allEdgesValid = edges.every(edge => 
      edge.relation !== 'invalid' && 
      edge.relation === edge.expected
    );
    
    // Check that relations alternate properly
    const alternating = edges.every((edge, i) => 
      i === 0 || edge.relation !== edges[i - 1].relation
    );
    
    return allEdgesValid && alternating;
  };

  const recalculateEdges = async (nodes: WordNode[]): Promise<{ edges: WordEdge[], isValid: boolean }> => {
    // If we're back to just the starting words, return empty edges
    if (nodes.length <= 2) {
      return { edges: [], isValid: false };
    }

    // Create array of word pairs to check
    const wordPairs = nodes.slice(0, -1).map((node, index) => ({
      currentWord: node.word,
      nextWord: nodes[index + 1].word,
      sourceId: node.id,
      targetId: nodes[index + 1].id,
      index
    }));

    // Make single batch API call for all pairs (much faster than individual calls)
    const relationResults = await checkWordRelationsBatch(
      wordPairs.map(({ currentWord, nextWord }) => ({ word1: currentWord, word2: nextWord }))
    );

    // Process results sequentially to maintain validation logic
    const newEdges: WordEdge[] = [];
    
    for (let i = 0; i < wordPairs.length; i++) {
      const { sourceId, targetId } = wordPairs[i];
      const { relation, justification } = relationResults[i];

      if (i === 0) {
        // First edge - use the relation as both actual and expected
        newEdges.push({
          source: sourceId,
          target: targetId,
          relation,
          expected: relation,
          justification
        });
      } else {
        // For subsequent edges, use previous edge to determine expected relation
        const expectedRelation: RelationType = newEdges[i - 1].relation === 'rhyme' ? 'reason' : 'rhyme';
        newEdges.push({
          source: sourceId,
          target: targetId,
          relation,
          expected: expectedRelation,
          justification
        });
      }
    }

    const isValid = validateChain(newEdges);
    return { edges: newEdges, isValid };
  };

  /**
   * Recalculate only specific edges (optimized for incremental updates).
   * Only recalculates edges starting from startIndex, keeping previous edges intact.
   */
  const recalculateEdgesFromIndex = async (
    nodes: WordNode[], 
    startIndex: number,
    existingEdges: WordEdge[]
  ): Promise<{ edges: WordEdge[], isValid: boolean }> => {
    if (nodes.length <= 2 || startIndex >= nodes.length - 1) {
      return { edges: existingEdges, isValid: validateChain(existingEdges) };
    }

    // Only recalculate edges from startIndex onwards
    const wordPairs = [];
    for (let i = startIndex; i < nodes.length - 1; i++) {
      wordPairs.push({
        currentWord: nodes[i].word,
        nextWord: nodes[i + 1].word,
        sourceId: nodes[i].id,
        targetId: nodes[i + 1].id,
        index: i
      });
    }

    if (wordPairs.length === 0) {
      return { edges: existingEdges, isValid: validateChain(existingEdges) };
    }

    // Make single batch API call for affected pairs only
    const relationResults = await checkWordRelationsBatch(
      wordPairs.map(({ currentWord, nextWord }) => ({ word1: currentWord, word2: nextWord }))
    );

    // Keep existing edges before startIndex
    const newEdges = existingEdges.slice(0, startIndex);
    
    // Process new edges
    for (let i = 0; i < wordPairs.length; i++) {
      const { sourceId, targetId } = wordPairs[i];
      const { relation, justification } = relationResults[i];

      let expectedRelation: RelationType;
      
      if (i === 0 && startIndex === 0) {
        // First edge overall - use the relation as both actual and expected
        expectedRelation = relation;
      } else if (i === 0 && startIndex > 0) {
        // First new edge after existing edges - determine expected from previous edge's actual relation
        const prevEdge = existingEdges[startIndex - 1];
        expectedRelation = prevEdge.relation === 'rhyme' ? 'reason' : 'rhyme';
      } else {
        // Subsequent edges - determine expected from previous new edge's actual relation
        const prevEdge = newEdges[newEdges.length - 1];
        expectedRelation = prevEdge.relation === 'rhyme' ? 'reason' : 'rhyme';
      }
      
      newEdges.push({
        source: sourceId,
        target: targetId,
        relation,
        expected: expectedRelation,
        justification
      });
    }

    const isValid = validateChain(newEdges);
    return { edges: newEdges, isValid };
  };

  const handleCancel = async (nodeId: string) => {
    const nodeIndex = gameState.nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex <= 0 || nodeIndex >= gameState.nodes.length - 1) return;
    
    // Mark this validation as cancelled
    activeValidationsRef.current.delete(nodeId);
    
    // Remove the pending word
    const updatedNodes = gameState.nodes.filter(n => n.id !== nodeId);
    
    // Update UI immediately
    setGameState(prev => ({
      ...prev,
      nodes: updatedNodes,
      edges: updatedNodes.length <= 2 ? [] : prev.edges,
      isSolved: false
    }));

    // If we're back to just start and end words, we're done
    if (updatedNodes.length <= 2) return;

    // Recalculate edges from the point before the cancelled word
    // This ensures we reconnect the chain properly
    try {
      const { edges: newEdges, isValid } = await recalculateEdgesFromIndex(
        updatedNodes,
        nodeIndex - 1, // Start from edge before cancelled word
        gameState.edges.slice(0, nodeIndex - 1) // Keep edges before cancelled word
      );
      
      setGameState(prev => {
        // Verify the nodes haven't changed (e.g., another cancel happened)
        if (prev.nodes.length !== updatedNodes.length) {
          return prev;
        }
        return {
          ...prev,
          edges: newEdges,
          isSolved: isValid && updatedNodes[updatedNodes.length - 1].word === prev.endWord
        };
      });
    } catch (error) {
      console.error('Error during edge recalculation:', error);
      // Fallback: recalculate all edges
      try {
        const { edges: newEdges, isValid } = await recalculateEdges(updatedNodes);
        setGameState(prev => {
          // Verify the nodes haven't changed
          if (prev.nodes.length !== updatedNodes.length) {
            return prev;
          }
          return {
            ...prev,
            edges: newEdges,
            isSolved: isValid && updatedNodes[updatedNodes.length - 1].word === prev.endWord
          };
        });
      } catch (fallbackError) {
        console.error('Error during fallback recalculation:', fallbackError);
      }
    }
  };

  const handleDelete = async (nodeId: string) => {
    const nodeIndex = gameState.nodes.findIndex(n => n.id === nodeId);
    if (nodeIndex <= 0 || nodeIndex >= gameState.nodes.length - 1) return;

    const updatedNodes = gameState.nodes.filter(n => n.id !== nodeId);
    
    // Update UI immediately
    setGameState(prev => ({
      ...prev,
      nodes: updatedNodes,
      edges: updatedNodes.length <= 2 ? [] : prev.edges,
      isSolved: false
    }));

    // If we're back to just start and end words, we're done
    if (updatedNodes.length <= 2) return;

    // Recalculate edges in the background
    try {
      const { edges: newEdges, isValid } = await recalculateEdges(updatedNodes);
      
      setGameState(prev => ({
        ...prev,
        edges: newEdges,
        isSolved: isValid && updatedNodes[updatedNodes.length - 1].word === prev.endWord
      }));
    } catch (error) {
      console.error('Error during edge recalculation:', error);
    }
  };

  const handleAddWord = async () => {
    if (!newWord.trim()) return;

    const newId = uuidv4();
    const endNode = gameState.nodes[gameState.nodes.length - 1];
    const insertIndex = gameState.nodes.length - 1; // Insert before end word

    // Insert new node before the end word with pending validation
    const updatedNodes = [
      ...gameState.nodes.slice(0, -1),
      { id: newId, word: newWord.toLowerCase(), pendingValidation: true },
      endNode
    ];

    // Track this validation
    activeValidationsRef.current.add(newId);

    // Update state immediately to show the new word with loading state
    setGameState(prev => ({
      ...prev,
      nodes: updatedNodes,
    }));

    setNewWord('');

    try {
      // Only recalculate edges from the insertion point (2 edges: before and after new word)
      // This is much faster than recalculating all edges
      const { edges: newEdges, isValid } = await recalculateEdgesFromIndex(
        updatedNodes,
        insertIndex - 1, // Start from edge before new word
        gameState.edges
      );

      // Only update if this validation is still active (not cancelled)
      if (activeValidationsRef.current.has(newId)) {
        activeValidationsRef.current.delete(newId);
        
        // Update state with validation results
        setGameState(prev => {
          // Double-check the node still exists
          if (!prev.nodes.find(n => n.id === newId)) {
            return prev;
          }
          
          const nodes = prev.nodes.map(node => 
            node.id === newId ? { ...node, pendingValidation: false } : node
          );
          return {
            ...prev,
            nodes,
            edges: newEdges,
            isSolved: isValid && nodes[nodes.length - 1].word === prev.endWord
          };
        });
      }
    } catch (error) {
      // Only handle error if validation is still active
      if (activeValidationsRef.current.has(newId)) {
        activeValidationsRef.current.delete(newId);
        console.error('Error during edge recalculation:', error);
        // Remove the word if validation fails
        setGameState(prev => {
          // Check if node still exists before removing
          if (!prev.nodes.find(n => n.id === newId)) {
            return prev;
          }
          return {
            ...prev,
            nodes: prev.nodes.filter(node => node.id !== newId),
            edges: prev.edges.slice(0, -1)
          };
        });
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newWord.trim()) {
      handleAddWord();
    }
  };

  const handleInputBlur = () => {
    if (newWord.trim()) {
      handleAddWord();
    }
  };

  const handleFlipWords = async () => {
    // Don't allow flipping if any words are being validated
    if (gameState.nodes.some(node => node.pendingValidation)) {
      return;
    }

    // Reverse all nodes
    const reversedNodes = [...gameState.nodes].reverse();
    const newStartWord = gameState.endWord;
    const newEndWord = gameState.startWord;
    
    // If we only have start/end words, just update state
    if (reversedNodes.length <= 2) {
      setGameState(prev => ({
        ...prev,
        nodes: reversedNodes,
        edges: [],
        startWord: newStartWord,
        endWord: newEndWord,
        isSolved: false
      }));
      return;
    }

    // Mark all non-start/end nodes as pending validation
    const nodesWithPending = reversedNodes.map((node, index) => 
      index === 0 || index === reversedNodes.length - 1
        ? node
        : { ...node, pendingValidation: true }
    );

    // Update state immediately with pending validation
    setGameState(prev => ({
      ...prev,
      nodes: nodesWithPending,
      startWord: newStartWord,
      endWord: newEndWord,
      edges: [],
      isSolved: false
    }));

    try {
      // Calculate edges in the background
      const { edges: newEdges, isValid } = await recalculateEdges(reversedNodes);
      
      setGameState(prev => ({
        ...prev,
        nodes: prev.nodes.map(node => ({ ...node, pendingValidation: false })),
        edges: newEdges,
        isSolved: isValid && reversedNodes[reversedNodes.length - 1].word === newEndWord
      }));
    } catch (error) {
      console.error('Error during flip recalculation:', error);
      // Revert to original state if validation fails
      setGameState(prev => ({
        ...prev,
        nodes: gameState.nodes,
        edges: gameState.edges,
        startWord: gameState.startWord,
        endWord: gameState.endWord,
        isSolved: gameState.isSolved
      }));
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = gameState.nodes.findIndex(node => node.id === active.id);
    const newIndex = gameState.nodes.findIndex(node => node.id === over.id);

    // Don't allow moving the start or end words or words being validated
    if (oldIndex === 0 || oldIndex === gameState.nodes.length - 1 ||
        newIndex === 0 || newIndex === gameState.nodes.length - 1 ||
        gameState.nodes[oldIndex].pendingValidation) {
      return;
    }

    const updatedNodes = arrayMove(gameState.nodes, oldIndex, newIndex);
    
    // Mark the moved node as pending validation
    const movedNodeId = updatedNodes[newIndex].id;
    const nodesWithPending = updatedNodes.map(node => 
      node.id === movedNodeId ? { ...node, pendingValidation: true } : node
    );

    // Track this validation
    activeValidationsRef.current.add(movedNodeId);

    // Update state immediately with pending validation
    setGameState(prev => ({
      ...prev,
      nodes: nodesWithPending,
    }));
    
    try {
      // Only recalculate edges from the minimum affected index
      // This optimizes by only checking edges that could have changed
      const affectedStartIndex = Math.min(oldIndex, newIndex);
      const { edges: newEdges, isValid } = await recalculateEdgesFromIndex(
        updatedNodes,
        affectedStartIndex,
        gameState.edges
      );
      
      // Only update if this validation is still active
      if (activeValidationsRef.current.has(movedNodeId)) {
        activeValidationsRef.current.delete(movedNodeId);
        
        setGameState(prev => {
          // Verify the node still exists and is still pending
          const node = prev.nodes.find(n => n.id === movedNodeId);
          if (!node || !node.pendingValidation) {
            return prev;
          }
          
          return {
            ...prev,
            nodes: prev.nodes.map(node => 
              node.id === movedNodeId ? { ...node, pendingValidation: false } : node
            ),
            edges: newEdges,
            isSolved: isValid && updatedNodes[updatedNodes.length - 1].word === prev.endWord
          };
        });
      }
    } catch (error) {
      // Only handle error if validation is still active
      if (activeValidationsRef.current.has(movedNodeId)) {
        activeValidationsRef.current.delete(movedNodeId);
        console.error('Error during reordering:', error);
        // Revert to previous state if validation fails
        setGameState(prev => {
          const node = prev.nodes.find(n => n.id === movedNodeId);
          if (!node || !node.pendingValidation) {
            return prev;
          }
          return {
            ...prev,
            nodes: gameState.nodes,
            edges: gameState.edges
          };
        });
      }
    }
  };

  const activeNode = activeId ? gameState.nodes.find(node => node.id === activeId) : null;

  const handleReset = () => {
    const [newStartWord, newEndWord] = getInitialWords();
    setGameState({
      nodes: [
        { id: '1', word: newStartWord },
        { id: '2', word: newEndWord }
      ],
      edges: [],
      startWord: newStartWord,
      endWord: newEndWord,
      isSolved: false
    });
    setShowCompletion(false);
  };

  const focusInput = () => {
    const input = document.querySelector('.MuiInputBase-input') as HTMLInputElement;
    if (input) {
      setInputAttentionKey(prev => prev + 1);
      input.focus();
    }
  };

  const handleCloseJustification = () => {
    setSelectedEdgeIndex(null);
  };

  const handleEdgeClick = (index: number) => {
    if (gameState.edges[index]?.justification) {
      setSelectedEdgeIndex(index);
    } else if (!gameState.edges[index]?.expected) {
      focusInput();
    }
  };

  const handleTitleFlip = () => {
    setIsTitleFlipped(prev => !prev);
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      margin: 'auto', 
      p: { xs: 1.5, sm: 3, md: 4 },
      minHeight: '100vh'
    }}>
      <HeaderContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'center', sm: 'flex-start' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          mb: 2,
          position: 'relative',
          pt: 1
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            position: 'relative',
            ml: { xs: 0, sm: 1 }
          }}>
            <TitleCard sx={{ 
              transform: { 
                xs: 'rotate(-2deg) translateY(-1px)',
                sm: 'rotate(-3deg) translateY(-2px)'
              },
              backgroundColor: '#FFE4A3',
              animation: isTitleFlipped ? `${bounceRight} 0.5s ease-out` : `${bounceLeft} 0.5s ease-out`,
            }}>
              {isTitleFlipped ? 'REASON' : 'RHYME'}
            </TitleCard>
            <Box
              onClick={handleTitleFlip}
              sx={{
                backgroundColor: '#98D87D',
                padding: (theme) => theme.spacing(0.5, 1.5),
                border: '3px solid black',
                boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
                transform: 'translate(-2px, -2px)',
                display: 'inline-block',
                transition: 'all 0.2s ease',
                minWidth: '40px',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translate(0px, 0px)',
                  boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
                },
                '&:active': {
                  transform: 'translate(1px, 1px)',
                  boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)',
                }
              }}
            >
              <Typography sx={{ 
                fontFamily: '"Public Sans", Arial, sans-serif',
                fontWeight: 900,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                lineHeight: 1,
                marginTop: '-2px',
                color: 'black',
              }}>
                OR
              </Typography>
            </Box>
            <TitleCard sx={{ 
              transform: { 
                xs: 'rotate(2deg) translateY(1px)',
                sm: 'rotate(3deg) translateY(2px)'
              },
              backgroundColor: '#FFE4A3',
              animation: isTitleFlipped ? `${bounceLeft} 0.5s ease-out` : `${bounceRight} 0.5s ease-out`,
            }}>
              {isTitleFlipped ? 'RHYME' : 'REASON'}
            </TitleCard>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HeaderButton
              onClick={handleReset}
              aria-label="reset game"
            >
              <Icon className="material-symbols-outlined" sx={{ transform: 'translateY(-1px)' }}>refresh</Icon>
            </HeaderButton>
            <HeaderButton
              onClick={() => setIsHelpOpen(true)}
              aria-label="game information"
            >
              <Icon className="material-symbols-outlined" sx={{ transform: 'translateY(-1px)' }}>help</Icon>
            </HeaderButton>
            <HeaderButton 
              onClick={handleFlipWords}
              aria-label="flip start and end words"
            >
              <Icon className="material-symbols-outlined" sx={{ transform: 'translateX(-1px)' }}>swap_vert</Icon>
            </HeaderButton>
          </Box>
        </Box>
      </HeaderContainer>

      <StyledDialog
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        aria-labelledby="game-rules-dialog"
        TransitionComponent={Transition}
        sx={(theme) => ({
          '& .MuiDialog-paper': {
            backgroundColor: '#FFF4DA',
            color: 'black',
            maxWidth: 480,
            margin: 2,
            marginTop: 4,
            padding: '20px 24px 16px',
            borderRadius: '8px',
            border: '3px solid black',
            boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)',
            transform: 'translate(-3px, -3px)',
            position: 'relative',
            overflow: 'visible',
            [theme.breakpoints.down('sm')]: {
              margin: theme.spacing(2),
              marginTop: theme.spacing(3),
              padding: '16px 20px 16px',
              maxWidth: 'calc(100% - 32px)',
            }
          },
          '& .MuiDialogTitle-root': {
            padding: 0,
            position: 'relative'
          },
          '& .MuiDialogContent-root': {
            padding: 0,
            marginTop: 0,
            overflow: 'visible'
          }
        })}
      >
        <IconButton
          onClick={() => setIsHelpOpen(false)}
          sx={{
            position: 'absolute',
            right: -12,
            top: -12,
            backgroundColor: '#FF4911',
            padding: '6px',
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            border: '3px solid black',
            boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)',
            transform: 'translate(-2px, -2px)',
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#FF4911',
              transform: 'translate(0px, 0px)',
              boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
              color: 'black',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ 
          fontFamily: '"Public Sans", Arial, sans-serif',
          fontWeight: 900,
          textAlign: 'center',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          overflow: 'visible'
        }}>
          <Box sx={{ 
            textAlign: 'center',
            position: 'relative',
            zIndex: 0,
          }}>
            <Box sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#98D87D',
              color: 'black',
              padding: '8px 16px',
              minHeight: '40px',
              borderRadius: '4px',
              border: '3px solid black',
              boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
              transform: 'translate(-2px, -2px)',
              fontFamily: '"Public Sans", Arial, sans-serif',
              fontWeight: 900,
              fontSize: '1rem',
              letterSpacing: '1px',
              whiteSpace: 'nowrap',
              [theme.breakpoints.down('sm')]: {
                fontSize: '0.9rem',
                padding: '6px 12px',
                minHeight: '36px'
              }
            })}>
              HOW TO PLAY
            </Box>
          </Box>
          <Box sx={{ 
            borderBottom: '3px solid black', 
            width: '85%',
            mx: 'auto'
          }} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            px: { xs: 1, sm: 2 },
            pt: 1.5,
            pb: { xs: 1, sm: 1.5 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'calc(100vh - 200px)', sm: 'auto' }
          }}>
            <Box sx={{ 
              backgroundColor: '#FFD15C',
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              borderRadius: '8px',
              border: '3px solid black',
              boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
              transform: 'translate(-2px, -2px)',
            }}>
              <Typography sx={{ 
                fontFamily: '"Public Sans", Arial, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                lineHeight: 1.5,
                textAlign: 'center'
              }}>
                Connect the start and end words by building a chain of alternating rhymes and related words ("reasons"). You can start with either a rhyme or a reason.
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ 
                mb: 2.5,
                fontFamily: '"Public Sans", Arial, sans-serif',
                fontWeight: 900,
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                color: 'black',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Icon className="material-symbols-outlined" sx={{ fontSize: '24px' }}>
                  route
                </Icon>
                Example Solutions
              </Typography>
              
              <Box sx={(theme) => ({ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 2.5, sm: 3 },
                mx: 'auto',
                width: '100%',
                maxWidth: { xs: '240px', sm: '100%' },
                [theme.breakpoints.down('sm')]: {
                  flex: '1 1 auto',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }
              })}>
                <Box sx={(theme) => ({
                  display: { xs: 'flex', sm: 'contents' },
                  flexDirection: 'column',
                  gap: 1,
                  minHeight: { xs: '280px', sm: 'auto' },
                  [theme.breakpoints.down('sm')]: {
                    flex: '1 1 auto',
                    minHeight: 0,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    pb: 1,
                    '& > div': {
                      flex: '0 0 auto'
                    }
                  }
                })}>
                  {[
                    {
                      words: ['peach', 'pit', 'sit', 'chair'],
                      connections: ['reason', 'rhyme', 'reason']
                    },
                    {
                      words: ['peach', 'teach', 'school', 'stool', 'chair'],
                      connections: ['rhyme', 'reason', 'rhyme', 'reason']
                    }
                  ].map((solution, solutionIndex) => (
                    <Box 
                      key={solutionIndex} 
                      sx={{
                        display: { 
                          xs: solutionIndex === activeExampleIndex ? 'flex' : 'none', 
                          sm: 'flex' 
                        },
                        flexDirection: 'column',
                        gap: 0.75,
                        width: '100%',
                        flex: '0 0 auto'
                      }}
                    >
                      {solution.words.map((word, index) => (
                        <React.Fragment key={index}>
                          <Box sx={{
                            backgroundColor: '#FFE4A3',
                            p: { xs: 0.5, sm: 0.75 },
                            borderRadius: '4px',
                            border: '2px solid black',
                            boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
                            transform: 'translate(-1px, -1px)',
                            textAlign: 'center',
                            width: '100%',
                            minHeight: { xs: '24px', sm: '28px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{
                              fontFamily: '"Public Sans", Arial, sans-serif',
                              fontWeight: 800,
                              fontSize: { xs: '0.75rem', sm: '0.9rem' },
                              textTransform: 'uppercase',
                              lineHeight: 1.2,
                              px: 0.5
                            }}>
                              {word}
                            </Typography>
                          </Box>
                          {index < solution.words.length - 1 && (
                            <Box sx={{
                              backgroundColor: '#98D87D',
                              padding: '2px',
                              borderRadius: '0px',
                              border: '2px solid black',
                              boxShadow: '2px 2px 0px 0px rgba(0,0,0,1)',
                              transform: 'translate(-1px, -1px)',
                              textAlign: 'center',
                              mx: 'auto',
                              minWidth: { xs: '64px', sm: '72px' },
                              height: { xs: '20px', sm: '24px' },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Typography sx={{
                                fontFamily: '"Public Sans", Arial, sans-serif',
                                fontWeight: 800,
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                lineHeight: 1.2
                              }}>
                                {solution.connections[index] === 'rhyme' ? 'RHYME' : 'REASON'}
                              </Typography>
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  ))}
                  
                  {/* Mobile Example Toggle */}
                  <Box sx={{ 
                    display: { xs: 'flex', sm: 'none' },
                    justifyContent: 'center',
                    gap: 1,
                    py: 1,
                    flex: '0 0 auto',
                    mt: 'auto'
                  }}>
                    {[0, 1].map((index) => (
                      <Box
                        key={index}
                        onClick={() => setActiveExampleIndex(index)}
                        sx={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: activeExampleIndex === index ? '#000' : '#CCC',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          padding: '6px',
                          margin: '-6px'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Typography sx={{ 
              textAlign: 'center',
              fontFamily: '"Public Sans", Arial, sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 'auto',
              pt: 2,
              flex: '0 0 auto',
              '& .material-symbols-outlined': {
                fontVariationSettings: '"FILL" 0, "wght" 700, "GRAD" 0, "opsz" 24',
                fontSize: '20px',
                color: 'black',
                transform: 'translateY(0)',
                display: 'flex',
                alignItems: 'center'
              }
            }}>
              <Icon className="material-symbols-outlined">
                schedule
              </Icon>
              A new puzzle is released daily at midnight
            </Typography>
          </Box>
        </DialogContent>
      </StyledDialog>

      <CompletionDialog
        open={showCompletion}
        onClose={() => setShowCompletion(false)}
        TransitionComponent={Transition}
      >
        <IconButton onClick={() => setShowCompletion(false)} aria-label="close">
          <CloseIcon />
        </IconButton>
        <Typography>
          You solved it in {gameState.nodes.length - 2} moves!
        </Typography>
        <Typography className="subtitle">
          Try another solution?
        </Typography>
        <Box className="button-container">
          <ActionButton onClick={handleShare}>
            Share
            <ShareIcon />
          </ActionButton>
          <ActionButton onClick={handleReset} bgColor="#FFD15C">
            <Icon className="material-symbols-outlined" sx={{ 
              transform: 'translateY(-1px)',
              mr: 0,
              fontSize: '28px',
              fontVariationSettings: '"FILL" 0, "wght" 800, "GRAD" 0, "opsz" 40'
            }}>refresh</Icon>
          </ActionButton>
        </Box>
      </CompletionDialog>

      <Snackbar
        open={showCopiedSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: '#FFD15C',
            color: 'black',
            fontFamily: '"Public Sans", Arial, sans-serif',
            fontWeight: 700,
            border: '3px solid black',
            boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
            transform: 'translate(-2px, -2px)',
          }
        }}
      />

      <Dialog
        open={selectedEdgeIndex !== null}
        onClose={handleCloseJustification}
        sx={(theme) => ({
          '& .MuiDialog-paper': {
            backgroundColor: '#FFF4DA',
            color: 'black',
            maxWidth: 400,
            margin: 2,
            marginTop: 4,
            padding: '24px 24px 24px',
            borderRadius: '8px',
            border: '3px solid black',
            boxShadow: '6px 6px 0px 0px rgba(0,0,0,1)',
            transform: 'translate(-3px, -3px)',
            position: 'relative',
            overflow: 'visible',
            [theme.breakpoints.down('sm')]: {
              margin: theme.spacing(2),
              marginTop: theme.spacing(3),
              padding: '20px 20px 20px',
              maxWidth: 'calc(100% - 32px)',
            }
          },
          '& .MuiDialogTitle-root': {
            padding: 0,
            paddingBottom: 2,
            position: 'relative'
          },
          '& .MuiDialogContent-root': {
            padding: 0,
            marginTop: 3,
            overflow: 'visible'
          }
        })}
      >
        <IconButton
          onClick={handleCloseJustification}
          sx={{
            position: 'absolute',
            right: -12,
            top: -12,
            backgroundColor: '#FF4911',
            padding: '6px',
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            border: '3px solid black',
            boxShadow: '3px 3px 0px 0px rgba(0,0,0,1)',
            transform: 'translate(-2px, -2px)',
            zIndex: 1,
            '&:hover': {
              backgroundColor: '#FF4911',
              transform: 'translate(0px, 0px)',
              boxShadow: '1px 1px 0px 0px rgba(0,0,0,1)',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
              color: 'black',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ 
          fontFamily: '"Public Sans", Arial, sans-serif',
          fontWeight: 900,
          textAlign: 'center',
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          position: 'relative',
          overflow: 'visible'
        }}>
          {selectedEdgeIndex !== null && (
            <>
              <Box sx={{ 
                textAlign: 'center',
                position: 'relative',
                zIndex: 0,
                mb: 2
              }}>
                <Box
                  sx={(theme) => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: gameState.edges[selectedEdgeIndex]?.relation === 'invalid' 
                      ? '#FF4911' 
                      : '#98D87D',
                    color: 'black',
                    padding: '8px 16px',
                    minHeight: '40px',
                    borderRadius: '4px',
                    border: '3px solid black',
                    boxShadow: '4px 4px 0px 0px rgba(0,0,0,1)',
                    transform: 'translate(-2px, -2px)',
                    fontFamily: '"Public Sans", Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: '1rem',
                    letterSpacing: '1px',
                    whiteSpace: 'nowrap',
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '0.9rem',
                      padding: '6px 12px',
                      minHeight: '36px'
                    }
                  })}
                >
                  {`${gameState.edges[selectedEdgeIndex]?.relation === 'invalid' ? 'INVALID' : 'VALID'} ${gameState.edges[selectedEdgeIndex]?.expected?.toUpperCase()}`}
                </Box>
              </Box>
              <Box sx={{ 
                borderBottom: '3px solid black', 
                pb: 2,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 900,
                lineHeight: 1.4
              }}>
                {gameState.nodes[selectedEdgeIndex].word.toUpperCase()} → {gameState.nodes[selectedEdgeIndex + 1].word.toUpperCase()}
              </Box>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedEdgeIndex !== null && (
            <Typography sx={{ 
              fontFamily: '"Public Sans", Arial, sans-serif',
              fontWeight: 700,
              textAlign: 'center',
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              px: { xs: 1, sm: 2 }
            }}>
              {gameState.edges[selectedEdgeIndex]?.justification?.split('. ').join('.\n\n')}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {gameState.isSolved && (
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            numberOfPieces={400}
            recycle={false}
            gravity={0.5}
            initialVelocityY={40}
            initialVelocityX={15}
            colors={['#FFD15C', '#98D87D', '#FF4911', '#FFE4A3']}
            tweenDuration={100}
            friction={0.99}
            confettiSource={{
              x: windowSize.width / 2,
              y: windowSize.height / 2,
              w: 0,
              h: 0
            }}
          />
        )}
        <SortableContext
          items={gameState.nodes.map(node => node.id)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={2} alignItems="center">
            {gameState.nodes.map((node, index) => (
              <React.Fragment key={node.id}>
                <SortableWordTile
                  node={node}
                  index={index}
                  isFirst={index === 0}
                  isLast={index === gameState.nodes.length - 1}
                  isSolved={gameState.isSolved}
                  onDelete={() => handleDelete(node.id)}
                  onCancel={node.pendingValidation ? () => handleCancel(node.id) : undefined}
                />
                
                {index < gameState.nodes.length - 1 && (
                  <Edge
                    status={
                      !gameState.edges[index]
                        ? 'none'
                        : gameState.edges[index].relation === 'invalid'
                          ? 'invalid'
                          : gameState.edges[index].relation === gameState.edges[index].expected
                            ? 'valid'
                            : 'none'
                    }
                  >
                    <Typography 
                      variant="body2" 
                      className="edge-label"
                      onClick={() => handleEdgeClick(index)}
                      sx={{
                        fontFamily: '"Public Sans", Arial, sans-serif',
                        fontWeight: 900,
                        fontSize: '1.25rem',
                        letterSpacing: '3px',
                        lineHeight: 1,
                        marginTop: '-2px',
                      }}
                    >
                      {gameState.edges[index]?.expected?.toUpperCase() || (
                        <AddIcon 
                          sx={{ 
                            fontSize: '24px',
                            transform: 'translateY(-1px)',
                            stroke: 'currentColor',
                            '& path': {
                              strokeWidth: 1,
                            }
                          }}
                        />
                      )}
                    </Typography>
                  </Edge>
                )}
              </React.Fragment>
            ))}

            {!gameState.isSolved && (
              <WordInput 
                elevation={0}
                key={inputAttentionKey}
                className="attention"
              >
                <TextField
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder="ENTER A WORD"
                  disabled={loading}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  variant="standard"
                />
              </WordInput>
            )}
          </Stack>
        </SortableContext>

        <DragOverlay>
          {activeNode ? (
            <WordTile
              elevation={0}
              sx={{
                backgroundColor: '#FFE4A3',
                opacity: 0.8,
                transform: 'scale(1.05) !important',
                cursor: 'grabbing',
                '& .delete-button': {
                  display: 'none',
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'black',
                  fontFamily: '"Public Sans", Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: (theme) => {
                    const length = activeNode.word.length;
                    if (length <= 4) return '2rem';
                    if (length <= 6) return '1.75rem';
                    if (length <= 8) return '1.5rem';
                    if (length <= 10) return '1.25rem';
                    return '1rem';
                  },
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {activeNode.word}
              </Typography>
            </WordTile>
          ) : null}
        </DragOverlay>
      </DndContext>
      <Box sx={{ 
        textAlign: 'center', 
        mt: { xs: 3, sm: 4 },
        mb: { xs: 2, sm: 3 },
        color: 'black',
        '& a': {
          color: 'inherit',
          textDecoration: 'none',
          fontWeight: 700,
          '&:hover': {
            textDecoration: 'underline'
          }
        },
        fontFamily: '"Public Sans", Arial, sans-serif'
      }}>
        Made with ☕️ by <a href="https://www.jesterlabs.ai" target="_blank" rel="noopener noreferrer">🃏 jesterlabs</a>
      </Box>
    </Box>
  );
};

export default Game; 